import { getSessionVariable, removeSessionVariable, setSessionVariable } from '@services/storage';

import { SessionTradeIn, TradeIn, TradeInCriteriaResponsesType, TradeInProductType } from '@app-types/trade-in';

import { TRADE_IN_SESSION_KEY, TradeInStatus, TradeInStep } from '@constants/trade-in';

type ReturnTradeInStep = {
    [TradeInStep.NOT_STARTED]: TradeInProductType | undefined;
    [TradeInStep.SEARCH]: TradeInProductType | undefined;
    [TradeInStep.FORM]: TradeInCriteriaResponsesType['questions'] | undefined;
    [TradeInStep.CONFIRMATION]: { amount: number; iban: string } | undefined;
    [TradeInStep.CONFIRMED]: undefined;
};

// Helpers

export function getTradeInStep<T extends TradeInStep>(step: T): ReturnTradeInStep[T] | undefined {
    const tradeIn = getSessionTradeIn();
    if (!tradeIn) {
        return undefined;
    }

    switch (step) {
        case TradeInStep.SEARCH:
            return tradeIn.recovery.product as ReturnTradeInStep[T];
        case TradeInStep.FORM:
            return tradeIn.recovery.criteria.questions as ReturnTradeInStep[T];
        case TradeInStep.CONFIRMATION:
            return tradeIn.recovery.estimation && tradeIn.product
                ? ({
                      amount: tradeIn.recovery.estimation.amount,
                  } as ReturnTradeInStep[T])
                : undefined;
        default:
            return undefined;
    }
}

export function getTradeInLastStep(): TradeInStep {
    const tradeIn = getSessionTradeIn();
    if (!tradeIn) {
        return TradeInStep.SEARCH;
    }

    if (tradeIn.status === TradeInStatus.COMPLETED) {
        return TradeInStep.CONFIRMED;
    }

    if (tradeIn.recovery.estimation) {
        return TradeInStep.CONFIRMATION;
    }

    if (tradeIn.recovery.criteria && tradeIn.recovery.criteria.questions.length > 0) {
        return TradeInStep.FORM;
    }

    return TradeInStep.SEARCH;
}

type SetTradeInStepInput =
    | TradeInProductType
    | TradeInCriteriaResponsesType['questions']
    | TradeInConfirmationInput
    | string;

export type TradeInConfirmationInput = NonNullable<TradeIn['recovery']['estimation']>;

export function setTradeInStep(step: TradeInStep, value: SetTradeInStepInput | undefined): void {
    let tradeIn: TradeIn;
    if (step === TradeInStep.SEARCH) {
        tradeIn = {
            status: TradeInStatus.UNFINISHED,
            recovery: {
                product: value as TradeInProductType,
                criteria: {
                    questions: [],
                    id: (value as TradeInProductType).id,
                },
            },
            product: {
                item: {
                    url: `${window.location.origin}${window.location.pathname}`,
                },
            },
        };
    } else {
        const session = getSessionTradeIn();
        if (!session) {
            throw new Error('No trade-in session found');
        }
        tradeIn = session;
        switch (step) {
            case TradeInStep.FORM:
                tradeIn.recovery.criteria.questions = value as TradeInCriteriaResponsesType['questions'];
                tradeIn.status = TradeInStatus.UNFINISHED;
                break;
            case TradeInStep.CONFIRMATION:
                tradeIn.recovery.estimation = {
                    amount: (value as TradeInConfirmationInput).amount,
                    id: (value as TradeInConfirmationInput).id,
                    questions: (value as TradeInConfirmationInput).questions,
                };
                tradeIn.status = TradeInStatus.UNFINISHED;
                tradeIn.recoveryId = (value as TradeInConfirmationInput).id;
                break;
            case TradeInStep.CONFIRMED:
                tradeIn.status = TradeInStatus.COMPLETED;
                break;
            default:
                break;
        }
    }
    setSessionTradeIn(tradeIn);
}

export function setTradeInBonus(bonus?: number): void {
    const tradeIn = getSessionTradeIn();

    if (!tradeIn) {
        return;
    }

    tradeIn.product.bonus = bonus;
    setSessionTradeIn(tradeIn);
}

// Utils

export function mapSessionTradeInFromStorage(raw: SessionTradeIn): TradeIn {
    return {
        recovery: {
            product: {
                name: raw.recovery.product.name,
                brand: raw.recovery.product.brand,
                id: raw.recovery.product.data.id,
            },
            criteria: {
                id: raw.recovery.criterias.id,
                questions: raw.recovery.criterias.items,
            },
            estimation: raw.recovery.estimation
                ? {
                      amount: raw.recovery.estimation.amount,
                      id: raw.recovery.estimation.id,
                      questions: raw.recovery.estimation.items.criteresReprise,
                  }
                : undefined,
        },
        product: {
            bonus: raw.product.bonus,
            item: {
                url: raw.product.item.url,
            },
        },
        status: raw.status,
        recoveryId: raw.recovery_id,
    };
}

export function mapSessionTradeInToStorage(app: TradeIn): SessionTradeIn {
    return {
        recovery: {
            product: {
                name: app.recovery.product.name,
                brand: app.recovery.product.brand,
                data: {
                    id: app.recovery.product.id,
                },
            },
            criterias: {
                id: app.recovery.criteria.id,
                items: app.recovery.criteria.questions,
            },
            estimation: app.recovery.estimation
                ? {
                      amount: app.recovery.estimation.amount,
                      id: app.recovery.estimation.id,
                      items: {
                          criteresReprise: app.recovery.estimation.questions,
                      },
                  }
                : undefined,
        },
        product: {
            bonus: app.product.bonus,
            item: {
                url: app.product.item.url,
            },
        },
        status: app.status,
        recovery_id: app.recoveryId,
    };
}

function setSessionTradeIn(tradeIn: TradeIn) {
    try {
        setSessionVariable(TRADE_IN_SESSION_KEY, JSON.stringify(mapSessionTradeInToStorage(tradeIn)));
    } catch (err) {
        console.error('Failed to set trade-in session:', err);
    }
}

function getSessionTradeIn(): TradeIn | undefined {
    try {
        const session = getSessionVariable(TRADE_IN_SESSION_KEY);
        if (!session) {
            return undefined;
        }
        return mapSessionTradeInFromStorage(JSON.parse(session) as SessionTradeIn);
    } catch (err) {
        console.error('Failed to get trade-in session:', err);
        return undefined;
    }
}

export function clearTradeInProduct() {
    removeSessionVariable(TRADE_IN_SESSION_KEY);
}

export function getTradeInRecoveryId(): string | null {
    const tradeIn = getSessionTradeIn();
    if (tradeIn?.status !== TradeInStatus.COMPLETED || !tradeIn.recoveryId) {
        return null;
    }
    return tradeIn.recoveryId;
}
